/**
 * Units of measure and conversions. Built-in metric pairs (g↔kg, ml↔l) plus conversions
 * the account defines, optionally per product ("1 bag of this flour = 12.5 kg").
 * A missing conversion is a result, not an exception: feasibility turns it into a
 * `missing_data` flag.
 * @module
 */
import { z } from 'zod';
import {
  type Qty,
  type QtyInput,
  type RoundingMode,
  isPositive,
  multiplyQty,
  divideQty,
  positiveQtyString,
  qty,
} from '../quantity/index.js';

/** Units every account has. `ea` = each (a count). */
export const BUILT_IN_UNITS = ['g', 'kg', 'ml', 'l', 'ea'] as const;
/** One of {@link BUILT_IN_UNITS}. */
export type BuiltInUnit = (typeof BUILT_IN_UNITS)[number];

/**
 * A unit code: a built-in unit or a custom pack unit such as `bag`, `tray`, `box_12`.
 * Lowercase letters, digits and `_`, starting with a letter, at most 32 characters.
 */
export type Uom = string;

const UOM_RE = /^[a-z][a-z0-9_]{0,31}$/;

/** True when `code` is a well-formed unit code. */
export function isUomCode(code: string): boolean {
  return UOM_RE.test(code);
}

/** True when `code` is a built-in unit. */
export function isBuiltInUnit(code: string): code is BuiltInUnit {
  return (BUILT_IN_UNITS as readonly string[]).includes(code);
}

/** zod schema for a unit code. */
export const uomCode = z.string().regex(UOM_RE, 'Unit codes are lowercase, e.g. "kg" or "bag"');

/**
 * One conversion: `1 <from> = <factor> <to>`. With `productId` it applies to that product
 * only and wins over a general conversion for the same pair.
 */
export interface UomConversion {
  productId?: string | null;
  from: Uom;
  to: Uom;
  /** Decimal string > 0. */
  factor: string;
}

/** zod schema for {@link UomConversion}. */
export const uomConversionSchema = z.object({
  productId: z.string().min(1).nullish(),
  from: uomCode,
  to: uomCode,
  factor: positiveQtyString,
});

/** The built-in metric conversions. */
export const BUILT_IN_CONVERSIONS: readonly UomConversion[] = [
  { from: 'kg', to: 'g', factor: '1000' },
  { from: 'l', to: 'ml', factor: '1000' },
];

/** Options for {@link convert}. */
export interface ConvertOptions {
  /** Product being converted; enables its product-specific conversions. */
  productId?: string | null;
  /**
   * Decimal places for a result that needed a division (an inverse conversion such as
   * g → kg with factor 1000 stored as kg → g). Default 6. Pure multiplications stay exact.
   */
  dp?: number;
  /** Rounding for that case. Default `half_up`. */
  rounding?: RoundingMode;
}

/** Result of {@link convert}. */
export type ConvertResult =
  | { ok: true; qty: Qty; /** Units visited, from first to last. */ path: Uom[] }
  | { ok: false; reason: 'missing_conversion'; from: Uom; to: Uom };

interface Edge {
  to: Uom;
  factor: Qty;
  /** True when this edge is the inverse of a stored conversion (needs division). */
  inverse: boolean;
  specific: boolean;
}

function buildGraph(conversions: readonly UomConversion[], productId: string | null | undefined) {
  const graph = new Map<Uom, Map<Uom, Edge>>();
  const put = (from: Uom, edge: Edge) => {
    const edges = graph.get(from) ?? new Map<Uom, Edge>();
    graph.set(from, edges);
    const existing = edges.get(edge.to);
    // Product-specific beats general; a stored direction beats an inverse.
    const better =
      !existing ||
      (edge.specific && !existing.specific) ||
      (edge.specific === existing.specific && existing.inverse && !edge.inverse);
    if (better) edges.set(edge.to, edge);
  };
  for (const c of [...BUILT_IN_CONVERSIONS, ...conversions]) {
    const specific = c.productId !== undefined && c.productId !== null;
    if (specific && c.productId !== productId) continue;
    if (!isUomCode(c.from) || !isUomCode(c.to)) {
      throw new RangeError(`Bad unit in conversion ${c.from} → ${c.to}`);
    }
    const factor = qty(c.factor);
    if (!isPositive(factor)) {
      throw new RangeError(`Conversion factor must be > 0 (${c.from} → ${c.to})`);
    }
    if (c.from === c.to) continue;
    put(c.from, { to: c.to, factor, inverse: false, specific });
    put(c.to, { to: c.from, factor, inverse: true, specific });
  }
  return graph;
}

/**
 * Converts `amount` from one unit to another, directly or through **one** intermediate
 * unit (bag → kg → g). Returns `{ ok: false, reason: 'missing_conversion' }` when no
 * route exists. Throws only on malformed conversions (bad unit code, factor ≤ 0).
 *
 * Route choice is deterministic: a direct conversion first; otherwise the route with the
 * fewest inverse steps, then the most product-specific steps, then the alphabetically
 * first intermediate unit.
 */
export function convert(
  amount: QtyInput,
  from: Uom,
  to: Uom,
  conversions: readonly UomConversion[] = [],
  options: ConvertOptions = {},
): ConvertResult {
  const value = qty(amount);
  if (from === to) return { ok: true, qty: value, path: [from] };
  const graph = buildGraph(conversions, options.productId);
  const precision = { dp: options.dp ?? 6, rounding: options.rounding ?? 'half_up' } as const;

  const apply = (v: Qty, steps: Edge[]): Qty => {
    let out = v;
    let divided = false;
    for (const e of steps) {
      if (e.inverse) {
        // Division: keep extra guard digits until the final rounding.
        out = divideQty(out, e.factor, { dp: precision.dp + 12, rounding: precision.rounding });
        divided = true;
      } else {
        out = multiplyQty(out, e.factor);
      }
    }
    return divided ? divideQty(out, 1, precision) : out;
  };

  const direct = graph.get(from)?.get(to);
  if (direct) return { ok: true, qty: apply(value, [direct]), path: [from, to] };

  let best: { via: Uom; steps: [Edge, Edge]; inverses: number; specifics: number } | undefined;
  for (const [via, first] of [...(graph.get(from) ?? new Map<Uom, Edge>())].sort(([a], [b]) =>
    a < b ? -1 : 1,
  )) {
    const second = graph.get(via)?.get(to);
    if (!second) continue;
    const inverses = Number(first.inverse) + Number(second.inverse);
    const specifics = Number(first.specific) + Number(second.specific);
    if (
      !best ||
      inverses < best.inverses ||
      (inverses === best.inverses && specifics > best.specifics)
    ) {
      best = { via, steps: [first, second], inverses, specifics };
    }
  }
  if (!best) return { ok: false, reason: 'missing_conversion', from, to };
  return { ok: true, qty: apply(value, best.steps), path: [from, best.via, to] };
}
